import { DiscoveredAssetsTabPaths } from "@features/assets-feature/constants";

/**
 * Routed overlay segments for the asset pages. Pages build their paths from these and `App.tsx`
 * builds its `allowedOverlayPath` lists from the helpers, so the two stay in sync.
 */
export const ASSET_SEGMENTS = {
  assetDetails: "asset-details",
  assetSupport: "asset-support",
  assetTags: "asset-tags",
  catalogProduct: "catalog-product",
  upload: "upload",
  purchasedAsset: "purchased-asset",
  rackOrder: "rack-order",
  transferOrder: "transfer-order",
  hlmOrder: "hlm-order",
} as const;

/** Page routes that host these overlays, without `/:prefix?`. `App.tsx` prefixes them. */
export const DISCOVERED_ASSETS_ROUTE = "assets/discovered-assets";
export const PURCHASED_ASSETS_ROUTE = "assets/purchased-assets";
export const INTEGRATION_CENTER_ORDERS_ROUTE =
  "assets/integration-center/orders";

/** Asset details dialog tab slugs; `assetDetailsRouteTabs` maps them to `AssetDialogTabs` values. */
export const ASSET_DETAILS_TABS = [
  "details",
  "contracts",
  "children",
  "tags-and-notes",
  "support",
] as const;
export type AssetDetailsTab = (typeof ASSET_DETAILS_TABS)[number];

export const CATALOG_PRODUCT_TABS = [
  "details",
  "locations",
  "contracts",
] as const;
export type CatalogProductTab = (typeof CATALOG_PRODUCT_TABS)[number];

export const PURCHASED_ASSET_TABS = ["details", "assets"] as const;
export type PurchasedAssetTab = (typeof PURCHASED_ASSET_TABS)[number];

/**
 * `viewBy` route slugs keyed by `PurchasedAssetsViewByTabIds` keys. Not `order`, which the global
 * order drawer owns.
 */
export const PURCHASED_ASSET_VIEW_BY_SLUGS = {
  purchaseDate: "purchase-date",
  manufacturer: "manufacturer",
  category: "category",
} as const;

/** Asset type route slugs keyed by `PurchasedAssetsTabIds` keys. */
export const PURCHASED_ASSET_TYPE_SLUGS = {
  all: "all",
  hardware: "hardware",
  softwareAndSubscriptions: "software-and-subscriptions",
} as const;

export const ASSET_DETAILS_PATH = `${ASSET_SEGMENTS.assetDetails}/:assetId`;
export const CATALOG_PRODUCT_PATH = `${ASSET_SEGMENTS.catalogProduct}/:product`;
export const PURCHASED_ASSET_PATH = `${ASSET_SEGMENTS.purchasedAsset}/:viewBy/:assetType/:assetRouteId`;
/** IT project assets tab: the project scopes the part number, so no view or asset type. */
export const PROJECT_PURCHASED_ASSET_PATH = `${ASSET_SEGMENTS.purchasedAsset}/:assetRouteId`;
export const RACK_ORDER_PATH = `${ASSET_SEGMENTS.rackOrder}/:orderRouteId`;
export const TRANSFER_ORDER_PATH = `${ASSET_SEGMENTS.transferOrder}/:orderRouteId`;
export const HLM_ORDER_PATH = `${ASSET_SEGMENTS.hlmOrder}/:orderRouteId`;

/** Pages that render `AssetDialogContainer`: details dialog, toolbar support and tags dialogs. */
export const assetDialogOverlayPaths = (base: string): string[] => [
  `${base}/${ASSET_DETAILS_PATH}/:tab?`,
  `${base}/${ASSET_SEGMENTS.assetSupport}`,
  `${base}/${ASSET_SEGMENTS.assetTags}`,
];

/** Pages that render `AssetProductDialog`. */
export const catalogProductOverlayPaths = (base: string): string[] => [
  `${base}/${CATALOG_PRODUCT_PATH}/:tab?`,
];

/** `base` ends at `discovered-assets`; overlays nest under the active tab. */
export const discoveredAssetsOverlayPaths = (base: string): string[] => {
  const tab = (id: string) => `${base}/${id}`;
  const assetTabs = [
    DiscoveredAssetsTabPaths.byAsset,
    DiscoveredAssetsTabPaths.byLocation,
    DiscoveredAssetsTabPaths.eolTimeline,
  ];
  return [
    ...assetTabs.flatMap((id) => assetDialogOverlayPaths(tab(id))),
    ...catalogProductOverlayPaths(tab(DiscoveredAssetsTabPaths.byProduct)),
    ...Object.values(DiscoveredAssetsTabPaths).map(
      (id) => `${tab(id)}/${ASSET_SEGMENTS.upload}`,
    ),
  ];
};

export const purchasedAssetsOverlayPaths = (base: string): string[] => [
  `${base}/${PURCHASED_ASSET_PATH}/:tab?`,
];

export const projectPurchasedAssetOverlayPaths = (base: string): string[] => [
  `${base}/${PROJECT_PURCHASED_ASSET_PATH}/:tab?`,
];

/** Integration order drawers on the integration center and IT project integration orders tabs. */
export const integrationOrderOverlayPaths = (base: string): string[] => [
  `${base}/${RACK_ORDER_PATH}`,
  `${base}/${TRANSFER_ORDER_PATH}`,
  `${base}/${HLM_ORDER_PATH}`,
];
